/* eslint-disable no-unused-vars */
/** @module Auth0ExternalLoginPageHelper */
/* eslint-disable import/extensions */
import Auth0Constants from '../../../utils/auth0-constants.mjs';
import Auth0Localizer from '../../../utils/auth0-localization-helper.mjs';
import Auth0UserSessionHelper from '../../../utils/auth0-session-helper.mjs';
import Auth0UtilsBrowser from '../../../utils/auth0-utils-browser.mjs';
import { Auth0PageBase } from '../../pages/auth0-page-template.mst.mjs';
import Auth0ExternalLoginFragment from '../../../../public/externalAppLogin.html.mst';
import Auth0Cdn from '../auth0-cdn-resource-resolver.mst.mjs';
import Auth0CookieHelper from '../../../utils/auth0-cookie-helper.mjs';
import logger from '../../../utils/logging/auth0-logger.mjs';

const CANCEL_CTX_LOGOUT = 'logout';
const CANCEL_CTX_CONTINUE_TO_SS = 'continue-to-ss';
const CANCEL_CTX_SWITCH_USER = 'logout-switch-user';

/**
 * @abstract
 */
class Auth0ExternalLoginPageHelper extends Auth0PageBase {
  constructor(options) {
    super(options);
    this._validate(options);

    this._externalAppId = options.externalAppId;
    this._externalAppDisplayName = options.externalAppDisplayName;
    this._externalAppUrl = options.externalAppUrl;
    this._externalAppParentAppName = options.externalAppParentAppName || this._whiteLabelHelper.appName;
    this._externalAppLogoUrl = options.externalAppLogoUrl || Auth0Cdn.AUTH0_LOGO_URL;
    this._externalAppPromptTextKey = options.externalAppPromptTextKey;
    this._externalAppContinueButtonTextKey = options.externalAppContinueButtonTextKey;
    this._externalAppNotYouTextKey = options.externalAppNotYouTextKey || 'external-app-not-you-link';
    this._externalAppLaunchType = options.externalAppLaunchType;
    this.audience = options.audience;
    this.clientId = options.clientId;

    this._pageStyles = [
      `${Auth0Cdn.AUTH0_STYLES_URL}/auth0-linking-confirmation-styles.css?cb=71c5d67`,
    ];
  }

  _validate(options) {
    if (!options?.externalAppId) {
      throw new Error('Auth0ExternalLoginPageHelper: missing options.externalAppId');
    }
    if (!options?.externalAppParentAppName && !this._whiteLabelHelper.appName) {
      throw new Error('Auth0ExternalLoginPageHelper: missing options.externalAppParentAppName (or white label name override)');
    }
  }

  get _externalAppLaunchType() {
    return this.__externalAppLaunchType ?? this.LAUNCH_TYPE_NEW_TAB;
  }

  set _externalAppLaunchType(value) {
    this.__externalAppLaunchType = value;
  }

  get LAUNCH_TYPE_POPUP_WINDOW() { return 'popup-window'; }

  get LAUNCH_TYPE_NEW_TAB() { return 'new-tab'; }

  get LAUNCH_TYPE_SAME_WINDOW() { return 'same-window'; }

  async _onAppLaunchSuccess() {
    // implement in derived classes for special handling
    // default behavior is nothing (the app has been launched tho...)
    await Promise.resolve();
  }

  _onAppLaunchError(errTextOrKey, errReplacementVars = null) {
    // implement in derived classes for special handling
    // default behavior... just display the error and do nothing else
    this._displayError(errTextOrKey, errReplacementVars);
  }

  get _continueToAppButton() {
    return document.getElementById('continue-to-external-app-button');
  }

  _disableContinueToAppButton = () => {
    this._continueToAppButton.disabled = true;
    this._continueToAppButton.classList.add('button-link-disabled');
  };

  async _afterAppLaunch() {
    // implement in derived classes for special handling
    // called in any case, error or success
    // default behavior is to disable the continue button and change the cancel and continue text to just continue text
    this._disableContinueToAppButton();
    Auth0Localizer.setElementLocalizedText({ id: 'external-app-cancel-and-continue', resourceIdOverride: 'external-app-continue-to-main-app' }, { parentAppName: this._externalAppParentAppName });
    await Promise.resolve();
  }

  _getContinueToAppUrl(tokens) {
    throw new Error('Auth0ExternalLoginPageHelper: must be implemented in derived class');
  }

  _removeAppLoginRequestContext = () => {
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_LEGACY_EXTERNAL_LOGIN_KEY);
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_EXTERNAL_APP_LOGIN_KEY);
  };

  _validateAppUrl(appUrl) {
    try {
      const validationUrl = new URL(appUrl);
      console.log(`Auth0ExternalLoginPageHelper => app url ${appUrl} validated...`);
    } catch (error) {
      logger.error(`Auth0ExternalLoginPageHelper => app url ${appUrl} is invalid, cancel launch...`);
      const errorWithKey = new Error(`The application url "${appUrl}" is not a valid url, aborting launch request..`);
      errorWithKey.key = 'external-app-invalid-url';
      errorWithKey.keyReplacementVars = { appName: this._externalAppDisplayName, appUrl };
      throw errorWithKey;
    }
  }

  // The launch click handler
  _continueToExternalAppHandler = async (event) => {
    if (this._continueToAppButton.disabled) {
      event.preventDefault();
      return;
    }
    this._removeAppLoginRequestContext();

    try {
      const appUrl = this._getContinueToAppUrl(this._tokens);
      this._validateAppUrl(appUrl);
      const launchType = this._externalAppLaunchType;
      console.log(`Auth0ExternalLoginPageHelper => continue to app @ ${appUrl} with launch type ${launchType}`);

      switch (launchType) {
        case this.LAUNCH_TYPE_POPUP_WINDOW:
          this._launchInPopupWindow(appUrl);
          break;
        case this.LAUNCH_TYPE_SAME_WINDOW:
          this._launchInSameWindow(appUrl);
          break;
        default:
          break;
      }
    } catch (error) {
      const errMsg = error.key || error.message;
      this._onAppLaunchError(errMsg, error.keyReplacementVars);
    } finally {
      await this._afterAppLaunch();
    }
  };

  // or if security prevents a launch click handler, you can configure the anchor element here and let the browser handle the click
  _decorateLaunchLinkForNewTab = () => {
    const link = document.getElementById('continue-to-external-app-button');
    link.setAttribute('target', '_blank');
    const appUrl = this._getContinueToAppUrl(this._tokens);
    this._validateAppUrl(appUrl);
    link.href = appUrl;
    console.log(`Auth0ExternalLoginPageHelper => _decorateLaunchLinkForNewTab: href @ ${link.href}`);
  };

  get _tokens() {
    return {
      accessToken: Auth0UserSessionHelper.userAccessTokenBodyByClientAndAudience(this.clientId, this.audience),
      refreshToken: Auth0UserSessionHelper.userRefreshTokenByClientAndAudience(this.clientId, this.audience),
    };
  }

  _launchInPopupWindow = async (appUrl) => {
    const newWindow = window.open(appUrl);
    if (!newWindow) {
      this._onAppLaunchError('external-app-popup-blocked', { appName: this._externalAppDisplayName, appUrl });
      return;
    }
    await this._onAppLaunchSuccess();
    window.location = '/';
  };

  _launchInSameWindow = async (appUrl) => {
    await this._onAppLaunchSuccess();
    window.location = appUrl;
  };

  _preLogoutActions(logoutType = Auth0Constants.LOGOUT_TYPE_REDIRECT) {
    super._preLogoutActions(logoutType);
    if (this._cancelContext !== CANCEL_CTX_SWITCH_USER) {
      // all cases but CANCEL_CTX_SWITCH_USER require removal of external auth request context
      this._removeAppLoginRequestContext();
    }
  }

  _switchUser = () => {
    console.log('_switchUser...');
    this._cancelContext = CANCEL_CTX_SWITCH_USER;
    this._logout();
  };

  _getAppLoginUrlWithOriginalQueryParams = () => {
    const originalSearchParams = window.location.search;
    if (!originalSearchParams) {
      return this._appLoginUrl;
    }

    const url = new URL(this._appLoginUrl, window.location.origin);
    const originalParams = Auth0UtilsBrowser.parseQueryString(originalSearchParams);

    // Preserve original query parameters
    originalParams.forEach((value, key) => {
      url.searchParams.set(key, value);
    });

    return url.toString();
  };

  _logout(logoutType = Auth0Constants.LOGOUT_TYPE_REDIRECT) {
    this._preLogoutActions(logoutType);

    Auth0UtilsBrowser.setOption({
      cookieName: Auth0Constants.AUTH0_FORCE_LOGOUT,
      value: { logoutType, shouldPreserveQueryParams: true },
      expirySec: 600,
      stringify: true,
    });

    // For switch user flow, preserve original query parameters
    const redirectUrl = this._cancelContext === CANCEL_CTX_SWITCH_USER
      ? this._getAppLoginUrlWithOriginalQueryParams()
      : this._appLoginUrl;

    document.location.href = redirectUrl;
  }

  _cancelAndContinueToSs = () => {
    console.log('_cancelAndContinue...');
    this._cancelContext = CANCEL_CTX_CONTINUE_TO_SS;
    this._removeAppLoginRequestContext();
    document.location.href = document.location.origin;
  };

  _cancelAndLogout = () => {
    console.log('_cancelAndLogout...');
    this._cancelContext = CANCEL_CTX_LOGOUT;
    // this._preLogoutActions();
    this._logout();
  };

  _localize = async () => {
    await super._localize();
    Auth0Localizer.setElementLocalizedText({ id: 'ss-external-app-login-as', resourceIdOverride: this._externalAppPromptTextKey });
    Auth0Localizer.setElementLocalizedText({ id: 'continue-to-external-app-button', resourceIdOverride: this._externalAppContinueButtonTextKey });
    Auth0Localizer.setElementLocalizedText({ id: 'ss-external-app-not-you-link', resourceIdOverride: this._externalAppNotYouTextKey });
    Auth0Localizer.setElementLocalizedText('external-app-cancel-and-continue', { parentAppName: this._externalAppParentAppName });
  };

  _initExternalLoginHtml = async () => {
    document.getElementById('external-app-logo').src = this._externalAppLogoUrl;

    // init the IdP "ID Card"
    document.getElementById('id-card-email').innerText = Auth0UserSessionHelper.userUserName;
    document.getElementById('id-card-picture').src = Auth0UserSessionHelper.userPicture;

    // add event handlers for start link and cancel link
    this._continueToAppButton.addEventListener('click', this._continueToExternalAppHandler);
    document.getElementById('external-app-cancel-and-continue').addEventListener('click', this._cancelAndContinueToSs);
    document.getElementById('ss-external-app-not-you-link').addEventListener('click', this._switchUser);
  };

  async _onUnload() {
    await super._onUnload();
    this._removeAppLoginRequestContext();
    await Promise.resolve();
  }

  async _onLoad() {
    await super._onLoad();
    // called when render of underlying template is complete
    this.toolbarVisible = true;
    this.actionLinkVisible = true;
    this.actionLinkText = Auth0Localizer.getLocalizedText('toolbar-action-cancel');
    this.actionLinkTextHandler = this._cancelAndLogout;
    await this.setBody(Auth0ExternalLoginFragment);
    await this._postSetBodyInitializer();
  }

  async _postSetBodyInitializer() {
    console.log('Auth0ExternalLoginPageHelper => _postSetBodyInitializer');
    const launchType = this._externalAppLaunchType;

    switch (launchType) {
      case this.LAUNCH_TYPE_NEW_TAB:
        this._decorateLaunchLinkForNewTab();
        break;
      default:
        break;
    }
  }

  /**
   * @function
   * @async
   * @description Directs the `Auth0LinkingPageHelper` to start the linking process. A valid SSO Identity Provider auth must have already happened before this page loads.
   */
  async _startUi() {
    await super._startUi();
    await this._initExternalLoginHtml();
    this._showPage();
  }
}

export { Auth0ExternalLoginPageHelper };
